import { Controller } from "@hotwired/stimulus"

// Dashboard: alterna vista (tabla/cards) EN VIVO sobre el DOM ya renderizado.
// El orden de filas lo resuelve el server (Presenter: críticos primero, fijo
// desde la reunión Tecnipak 21-08 — el chip "críticos primero" y el filtro
// "solo problemas" se quitaron; el resumen de alertas del tablero los
// reemplaza), así que este controller ya no reordena ni filtra.
//
// La lógica canónica y testeada vive en V2::Dashboard::Presenter (Ruby); este
// controller es su equivalente client-side para el preview. Sin bundle JS.
// Traspasos por fila en vista tarjetas (reunión Tecnipak 21-08): stepper del
// board (cuántas TARJETAS de traspaso por fila), preferencia del DISPOSITIVO
// (no cruza equipos ni va a la URL — el auto-refresh no debe re-escribirla),
// por eso localStorage y no query param como view. Ciclo 2 → 3 → 4 → 5: sin
// "auto" porque en desktop el grid responsive ya cae en 2 columnas.
const COLUMNS_KEY = "tp:v2:dash-cols"
const COLUMN_OPTIONS = ["2", "3", "4", "5"]
const DEFAULT_COLUMNS = "2"

export default class extends Controller {
  static values = {
    view: String,
    // Default contextual de la vista (móvil → "board"): lo decide el server
    // por UA y lo serializa el componente. Con default "table" para previews
    // y markup que no lo envía.
    defaultView: { type: String, default: "table" },
    // "2" | "3" | "4" | "5" = traspasos por fila elegidos por el usuario.
    cols: { type: String, default: DEFAULT_COLUMNS },
  }

  connect() {
    this.colsValue = this._storedColumns()
    this.render()
    // Botones/clases insertados o reemplazados por Turbo Streams (altas desde
    // modales): re-sincronizar estados con el DOM nuevo. El observer se apaga
    // durante render() (ver abajo) para no reaccionar a nuestros propios cambios.
    this._observer = new MutationObserver(() => this.render())
    this._observe()
    // Estado del botón/clase de pantalla completa (también cubre salir con Esc
    // y re-conectar tras un refresh del frame estando en fullscreen).
    this._onFullscreenChange = () => this._syncFullscreen()
    document.addEventListener("fullscreenchange", this._onFullscreenChange)
    this._syncFullscreen()
    // Donde el navegador no expone la API (iOS fuera de <video>) el botón sería
    // un control muerto: se oculta por clase en vez de por breakpoint.
    this.element.classList.toggle("tp-dash--no-fullscreen", !document.fullscreenEnabled)
  }

  disconnect() {
    this._observer?.disconnect()
    if (this._onFullscreenChange) {
      document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    }
  }

  // ── Pantalla completa ──
  // El target es el TURBO-FRAME que envuelve el board (no .tp-dash): así el
  // auto-refresh de 5 min reemplaza los hijos del frame sin salir de fullscreen
  // (si el elemento fullscreen fuera reemplazado, el browser lo abortaría).
  get fullscreenTarget() {
    return this.element.closest("turbo-frame") || this.element
  }

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.fullscreenTarget.requestFullscreen()
    }
  }

  _syncFullscreen() {
    const active = document.fullscreenElement === this.fullscreenTarget && !!document.fullscreenElement
    this.element.classList.toggle("tp-dash--fullscreen", active)
    this.element.querySelectorAll('[data-action*="v2--dashboard#toggleFullscreen"]').forEach((btn) => {
      btn.classList.toggle("is-active", active)
    })
  }

  _observe() {
    this._observer?.observe(this.element, { childList: true, subtree: true })
  }

  // ── Acciones (toolbar) ──
  setView(event) {
    this.viewValue = event.params.view
    // Persistir el modo en la URL: el auto-refresh de 5 min re-pide la URL actual
    // (window.location.href); sin este param el server cae al default del
    // contexto y la vista "rebota" en cada refresh. El default es CONTEXTUAL
    // (móvil → board): el param solo se dropea al elegir el default del
    // contexto (URL limpia sin rebote); cualquier otra elección queda fija.
    const url = new URL(window.location.href)
    if (event.params.view === this.defaultViewValue) {
      url.searchParams.delete("view")
    } else {
      url.searchParams.set("view", event.params.view)
    }
    history.replaceState(history.state, "", url)
  }

  // Stepper (▼ valor ▲ en la fila de filtros): recorre el ciclo 2 → 3 → 4 → 5
  // en ambas direcciones (con wrap: desde 5 "más" vuelve a 2, desde 2 "menos"
  // cae en 5).
  stepColumns(event) {
    // String(): Stimulus castea los params que parecen números.
    const direction = String(event.params.direction)
    const current = COLUMN_OPTIONS.indexOf(this.colsValue)
    const delta = direction === "down" ? -1 : 1
    this.colsValue = COLUMN_OPTIONS[(current + delta + COLUMN_OPTIONS.length) % COLUMN_OPTIONS.length]
    // try/catch: modo privado de Safari lanza al escribir localStorage.
    try {
      localStorage.setItem(COLUMNS_KEY, this.colsValue)
    } catch (_e) { /* preferencia no persistida; aplica solo a esta visita */ }
  }
  _storedColumns() {
    try {
      const stored = localStorage.getItem(COLUMNS_KEY)
      return COLUMN_OPTIONS.includes(stored) ? stored : DEFAULT_COLUMNS
    } catch (_e) {
      return DEFAULT_COLUMNS
    }
  }

  // ── Reacciones a cambios de estado ──
  viewValueChanged() { this.render() }
  colsValueChanged() { this.render() }

  render() {
    // Observer OFF durante el render: las mutaciones de clases dispararían
    // el observer en loop. takeRecords() descarta lo pendiente.
    this._observer?.disconnect()
    this.syncRootClasses()
    this.syncButtons()
    this._observer?.takeRecords()
    if (this.element.isConnected) this._observe()
  }

  syncRootClasses() {
    // El dashboard controla la vista en su propio elemento.
    const el = this.element
    el.classList.toggle("tp-dash--view-table", this.viewValue === "table")
    el.classList.toggle("tp-dash--view-board", this.viewValue === "board")
    // Columnas de tarjetas: la clase vive en el mismo root que la ve el CSS
    // (el selector se serializa en el toolbar, bajo este elemento).
    COLUMN_OPTIONS.forEach((n) => el.classList.toggle(`tp-dash--cols-${n}`, this.colsValue === n))
  }

  syncButtons() {
    this.setActive("setView", "view", this.viewValue)
    this.syncColsValue()
  }

  // Valor visible del stepper: el n° elegido. El server siempre renderiza el
  // default ("2") — la preferencia vive en el dispositivo — así que tras cada
  // auto-refresh del frame hay que re-escribirlo.
  syncColsValue() {
    this.element.querySelectorAll(".tp-dash-cols__value").forEach((el) => {
      if (el.textContent !== this.colsValue) el.textContent = this.colsValue
    })
  }

  setActive(method, param, value) {
    this.element.querySelectorAll(`[data-action*="v2--dashboard#${method}"]`).forEach((btn) => {
      btn.classList.toggle("is-active", btn.getAttribute(`data-v2--dashboard-${param}-param`) === value)
    })
  }
}
